import { useEffect, useState } from 'react';

import { FirebaseApp, getApp, getApps, initializeApp } from 'firebase/app';
import { getAuth, updateProfile } from 'firebase/auth';
import { getFirestore } from 'firebase/firestore';
import {
  RemoteConfig,
  fetchAndActivate,
  getNumber,
  getRemoteConfig,
  getString,
  isSupported,
} from 'firebase/remote-config';

const firebaseApneConfig = {
  apiKey: process.env.NEXT_PUBLIC_FIREBASE_APNE_API_KEY,
  authDomain: process.env.NEXT_PUBLIC_FIREBASE_APNE_AUTH_DOMAIN,
  projectId: process.env.NEXT_PUBLIC_FIREBASE_APNE_PROJECT_ID,
  appId: process.env.NEXT_PUBLIC_FIREBASE_APNE_APP_ID,
};

const firebaseConfig = {
  apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY,
  authDomain: process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID,
  appId: process.env.NEXT_PUBLIC_FIREBASE_APP_ID,
};

let firebaseApneApp: FirebaseApp;
let firebaseApp: FirebaseApp;

if (getApps().length === 0) {
  firebaseApneApp = initializeApp(firebaseApneConfig, 'apne');
  firebaseApp = initializeApp(firebaseConfig);
} else {
  firebaseApneApp = getApp('apne');
  firebaseApp = getApp();
}

export const firebaseAuth = getAuth(firebaseApneApp);

export const firestore = getFirestore(firebaseApneApp);

type RemoteConfigValue = {
  SHOW_AD_TAP_COUNT: number;
  CHAT_BOT_TESTER: string;
  SHOW_AD_CHAT_COUNT: number;
  /**
   * string
   */
  __PLACEHOLDER: string;
};

let remoteConfigInitialized = false;
let remoteConfigInitError: Error | null = null;
let remoteConfig: RemoteConfig | null = null;

async function safelyInitializeRemoteConfig(): Promise<RemoteConfig | null> {
  if (typeof window === 'undefined') {
    return null;
  }
  if (!('indexedDB' in window) || !window.indexedDB) {
    console.warn('IndexedDB is not available in this environment.');
    return null;
  }
  if (remoteConfigInitialized) {
    return remoteConfig;
  }

  try {
    const isSupportRemoteConfig = await isSupported();
    if (!isSupportRemoteConfig) {
      return null;
    }

    remoteConfig = getRemoteConfig(firebaseApp);
    if (remoteConfig) {
      remoteConfig.settings.minimumFetchIntervalMillis = 3600000;
      remoteConfigInitialized = true;
    }
    return remoteConfig;
  } catch (err) {
    remoteConfigInitError = err instanceof Error ? err : new Error(String(err));
    console.warn('Failed to initialize Remote Config:', remoteConfigInitError);
    return null;
  }
}
async function safelyFetchRemoteConfig(): Promise<boolean> {
  const config = await safelyInitializeRemoteConfig();
  if (!config) {
    return false;
  }

  const isSupportRemoteConfig = await isSupported();
  if (!isSupportRemoteConfig) {
    return false;
  }
  try {
    await fetchAndActivate(config);
    return true;
  } catch (err) {
    console.log('Remote Config fetch failed:', err);
    return false;
  }
}

export function useRemoteConfigValue<K extends keyof RemoteConfigValue>(
  key: K,
  defaultValue: RemoteConfigValue[K]
): RemoteConfigValue[K] {
  const [value, setValue] = useState<string | number>(defaultValue);

  useEffect(() => {
    if (
      typeof window === 'undefined' ||
      remoteConfigInitError ||
      !('indexedDB' in window) ||
      !window.indexedDB
    ) {
      setValue(defaultValue);
      return;
    }
    try {
      safelyFetchRemoteConfig()
        .then((success) => {
          if (!success || !remoteConfig) {
            setValue(defaultValue);
            return;
          }
          try {
            switch (key) {
              case 'SHOW_AD_TAP_COUNT': {
                const data = getNumber(remoteConfig, key);
                setValue(data);
                break;
              }
              case 'SHOW_AD_CHAT_COUNT': {
                const data = getNumber(remoteConfig, key);
                setValue(data);
                break;
              }
              case 'CHAT_BOT_TESTER': {
                const data = getString(remoteConfig, key);
                setValue(data);
                break;
              }
              default: {
                const data = getString(remoteConfig, key);
                setValue(data);
                break;
              }
            }
          } catch (err) {
            setValue(defaultValue);
          }
        })
        .catch(() => {
          setValue(defaultValue);
        });
    } catch (err) {
      setValue(defaultValue);
    }
  }, [key, defaultValue]);

  return value as RemoteConfigValue[K];
}

export const updateUserEmail = async (
  updateKey: string,
  updatePayload: string
) => {
  const auth = firebaseAuth;
  const currentUser = auth?.currentUser;

  if (!currentUser) {
    return alert('로그인을 진행해주세요');
  }

  if (!currentUser?.emailVerified) {
    return alert('이메일 인증을 완료해주세요.');
  }

  await updateProfile(currentUser, {
    [updateKey]: updatePayload,
  }).then();
};
